import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getImageUrl } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { NotificationBell } from '../components/NotificationBell';
import AdminFinancial from './AdminFinancial';
import AdminContent from './AdminContent';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';
import AdminMetrics from './admin/AdminMetrics';
import AdminAuditLog from './admin/AdminAuditLog';
import AdminPersonalApprovals from './admin/AdminPersonalApprovals';
import AdminProfessionalApprovals from './admin/AdminProfessionalApprovals';
import AdminVitalPassControl from './admin/AdminVitalPassControl';
import AdminPayouts from './admin/AdminPayouts';
import adminAxios from './admin/adminAxios';
import {
  Activity, Shield, LogOut, Home, Building2, Dumbbell, Users, CreditCard,
  TrendingUp, DollarSign, Loader2, Search, ToggleLeft, ToggleRight,
  Eye, ChevronDown, ChevronUp, RefreshCw, UserCheck, UserX, AlertCircle,
  Pencil, Save, X, Trash2, ClipboardCheck, MessageCircle, Phone, Check, CheckCircle2, Tag, ShieldCheck,
  Bell, UserPlus, Paperclip, Send, KeyRound, ScrollText, ChevronLeft, ChevronRight,
  Gauge, Database, Zap, Clock, Server, HardDrive, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Camera, Mail, Film, Stethoscope, Ticket
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// ==================== OVERVIEW ====================
const AdminOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitsData, setVisitsData] = useState(null);

  useEffect(() => {
    adminAxios.get(`${API}/admin/dashboard`).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
    adminAxios.get(`${API}/admin/visits-hourly?hours=24`).then(r => setVisitsData(r.data)).catch(() => {});
  }, []);

  if (loading) return <LoadingSpinner />;
  if (!data) return (
    <div className="flex items-center justify-center h-96">
      <p className="text-slate-500">Erro ao carregar dashboard</p>
    </div>
  );

  const stats = [
    { label: 'Empresas', value: data.total_companies ?? 0, icon: <Building2 className="w-5 h-5" />, color: 'blue' },
    { label: 'Academias', value: data.total_gyms ?? 0, icon: <Dumbbell className="w-5 h-5" />, color: 'orange' },
    { label: 'Alunos', value: data.total_students ?? 0, icon: <Users className="w-5 h-5" />, color: 'emerald' },
    { label: 'Receita Total', value: `R$ ${(data.total_revenue ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icon: <DollarSign className="w-5 h-5" />, color: 'violet' },
    { label: 'Visitas (24h)', value: data.visits_24h ?? 0, icon: <Eye className="w-5 h-5" />, color: 'sky' },
  ];

  const colorMap = {
    blue: 'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600',
    sky: 'bg-sky-50 text-sky-600',
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Painel Geral</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s, i) => (
          <Card key={s.label} className="border-slate-100">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-500">{s.label}</span>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[s.color]}`}>{s.icon}</div>
              </div>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-100 col-span-2">
          <CardHeader><CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Crescimento (6 meses)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.growth_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }} />
                  <Legend />
                  <Bar dataKey="empresas" fill="#2563EB" radius={[4, 4, 0, 0]} name="Empresas" />
                  <Bar dataKey="alunos" fill="#10B981" radius={[4, 4, 0, 0]} name="Alunos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Resumo Rápido</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500">Alunos ativos</span>
              <span className="font-bold text-slate-900">{data.active_students}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500">Pagamentos confirmados</span>
              <span className="font-bold text-emerald-600">{data.total_paid}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500">Pagamentos pendentes</span>
              <span className="font-bold text-orange-600">{data.total_pending}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-slate-50">
              <span className="text-sm text-slate-500">Novas empresas (30d)</span>
              <span className="font-bold text-slate-900">{data.recent_companies}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-slate-500">Novos alunos (30d)</span>
              <span className="font-bold text-slate-900">{data.recent_students}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Visits per Hour Chart */}
      {visitsData && (
        <Card className="border-slate-100">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Visitas por Hora (24h)</CardTitle>
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Total: <strong className="text-slate-900">{visitsData.total}</strong></span>
                <span className="text-slate-500">Pico: <strong className="text-sky-600">{visitsData.peak_hour} ({visitsData.peak_count})</strong></span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={visitsData.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11, fill: '#64748b' }} interval={1} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="visitas" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Visitas" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}


      {data.recent_payments?.length > 0 && (
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-base font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Últimos Pagamentos</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead><tr className="border-b border-slate-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Plano</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Valor</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Data</th>
              </tr></thead>
              <tbody>
                {data.recent_payments.map((p, i) => (
                  <tr key={p.transaction_id || p.payment_id || i} className="border-b border-slate-50">
                    <td className="px-5 py-3 text-sm font-medium text-slate-900">{p.plan_name}</td>
                    <td className="px-5 py-3 text-sm text-slate-700">R$ {p.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3"><StatusBadge status={p.payment_status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== COMPANIES ====================
const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [chargeModal, setChargeModal] = useState(null); // { company, plan_id, employee_count, custom_price, url, generating, copied }

  const fetchData = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/companies`).then(r => { setCompanies(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleStatus = async (company) => {
    const newStatus = !(company.is_active !== false);
    try {
      await adminAxios.put(`${API}/admin/companies/${company.company_id}/status`, { is_active: newStatus });
      toast.success(newStatus ? 'Empresa ativada' : 'Empresa inativada');
      fetchData();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const deleteCompany = async (companyId) => {
    if (!window.confirm('Tem certeza? Isso excluirá a empresa e todos os dados associados.')) return;
    try {
      await adminAxios.delete(`${API}/admin/companies/${companyId}`);
      toast.success('Empresa excluída');
      fetchData();
    } catch { toast.error('Erro ao excluir empresa'); }
  };

  const startEdit = (c) => {
    setEditing(c.company_id);
    setEditData({ cnpj: c.cnpj || '', razao_social: c.razao_social || '', cep: c.cep || '', street: c.street || '', number: c.number || '', city: c.city || '', phone: c.phone || '', email: c.email || '', rh_name: c.rh_name || '', rh_phone: c.rh_phone || '', rh_email: c.rh_email || '', contract_months: c.contract_months || '' });
  };

  const saveEdit = async (companyId) => {
    try {
      await adminAxios.put(`${API}/admin/companies/${companyId}/edit`, editData);
      toast.success('Empresa atualizada');
      setEditing(null);
      fetchData();
    } catch { toast.error('Erro ao salvar'); }
  };

  const openChargeModal = (c) => {
    setChargeModal({
      company: c,
      plan_id: 'annual',
      employee_count: c.contracted_employees || 6,
      custom_price: '',
      url: null,
      generating: false,
      copied: false,
    });
  };

  const generateChargeLink = async () => {
    if (!chargeModal) return;
    setChargeModal(prev => ({ ...prev, generating: true }));
    try {
      const payload = {
        plan_id: chargeModal.plan_id,
        employee_count: Number(chargeModal.employee_count),
        origin_url: window.location.origin,
      };
      if (Number(chargeModal.employee_count) > 100) {
        payload.custom_price_per_employee = parseFloat(chargeModal.custom_price);
      }
      const res = await adminAxios.post(`${API}/admin/companies/${chargeModal.company.company_id}/generate-checkout`, payload);
      setChargeModal(prev => ({ ...prev, url: res.data.url, amount: res.data.amount, generating: false }));
      toast.success('Link de cobrança gerado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar link');
      setChargeModal(prev => ({ ...prev, generating: false }));
    }
  };

  const copyChargeUrl = () => {
    if (!chargeModal?.url) return;
    navigator.clipboard.writeText(chargeModal.url);
    setChargeModal(prev => ({ ...prev, copied: true }));
    toast.success('Link copiado para a área de transferência');
    setTimeout(() => setChargeModal(prev => prev ? { ...prev, copied: false } : null), 2000);
  };

  const filtered = companies.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.admin_email?.toLowerCase().includes(search.toLowerCase()) ||
    c.cnpj?.includes(search)
  );

  const planLabel = (p) => ({ annual: 'Anual', biennial: '24 Meses' }[p] || 'Sem plano');

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Empresas Parceiras</h1>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome, email ou CNPJ..." className="pl-10" data-testid="admin-company-search" />
      </div>
      {filtered.length === 0 ? <EmptyState message="Nenhuma empresa encontrada" /> : (
        <div className="space-y-3">
          {filtered.map(c => (
            <Card key={c.company_id} className={`border-slate-100 ${c.is_active === false ? 'opacity-60' : ''}`}>
              <CardContent className="p-0">
                <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(expanded === c.company_id ? null : c.company_id)}>
                  <div className="flex items-center gap-3">
                    {c.logo ? (
                      <img src={getImageUrl(c.logo)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Building2 className="w-5 h-5 text-blue-600" /></div>
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">{c.razao_social || c.name}</p>
                      <p className="text-xs text-slate-500">{c.cnpj || 'Sem CNPJ'} | {planLabel(c.plan)} | {c.student_count} colaboradores</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={c.is_active !== false ? 'active' : 'inactive'} />
                    {expanded === c.company_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
                {expanded === c.company_id && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-3">
                    {editing === c.company_id ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[['cnpj','CNPJ'],['razao_social','Razão Social'],['cep','CEP'],['street','Rua'],['number','Número'],['city','Cidade'],['phone','Telefone'],['email','Email'],['rh_name','Representante RH'],['rh_phone','Tel. Representante'],['rh_email','Email Representante'],['contract_months','Meses Contrato']].map(([k,l]) => (
                          <div key={k} className="space-y-1">
                            <Label className="text-xs text-slate-500">{l}</Label>
                            <Input value={editData[k] || ''} onChange={e => setEditData({...editData, [k]: e.target.value})} className="h-8 text-sm" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div><span className="text-slate-500">ID:</span> <strong className="text-slate-700">{c.company_id}</strong></div>
                        <div><span className="text-slate-500">CNPJ:</span> <strong className="text-slate-700">{c.cnpj || '-'}</strong></div>
                        <div><span className="text-slate-500">Razão Social:</span> <strong className="text-slate-700">{c.razao_social || '-'}</strong></div>
                        <div><span className="text-slate-500">CEP:</span> <strong className="text-slate-700">{c.cep || '-'}</strong></div>
                        <div><span className="text-slate-500">Rua:</span> <strong className="text-slate-700">{c.street ? `${c.street}, ${c.number || ''}` : '-'}</strong></div>
                        <div><span className="text-slate-500">Cidade:</span> <strong className="text-slate-700">{c.city || '-'}</strong></div>
                        <div><span className="text-slate-500">Telefone:</span> <strong className="text-slate-700">{c.phone || '-'}</strong></div>
                        <div><span className="text-slate-500">Email:</span> <strong className="text-slate-700">{c.email || '-'}</strong></div>
                        <div><span className="text-slate-500">Representante RH:</span> <strong className="text-slate-700">{c.rh_name || '-'}</strong></div>
                        <div><span className="text-slate-500">Tel. Representante:</span> <strong className="text-slate-700">{c.rh_phone || '-'}</strong></div>
                        <div><span className="text-slate-500">Email Representante:</span> <strong className="text-slate-700">{c.rh_email || '-'}</strong></div>
                        <div><span className="text-slate-500">Contrato:</span> <strong className="text-slate-700">{c.contract_months ? `${c.contract_months} meses` : '-'}</strong></div>
                        <div><span className="text-slate-500">Plano:</span> <strong className="text-slate-700">{planLabel(c.plan)}</strong></div>
                        <div><span className="text-slate-500">Total pago:</span> <strong className="text-emerald-600">R$ {c.total_paid?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></div>
                        <div><span className="text-slate-500">Admin:</span> <strong className="text-slate-700">{c.admin_name || '-'}</strong></div>
                        <div><span className="text-slate-500">Criado em:</span> <strong className="text-slate-700">{c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</strong></div>
                      </div>
                    )}
                    <div className="flex gap-2">
                      {editing === c.company_id ? (
                        <>
                          <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); saveEdit(c.company_id); }}><Save className="w-4 h-4 mr-1" />Salvar</Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(null); }}><X className="w-4 h-4 mr-1" />Cancelar</Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); startEdit(c); }} data-testid={`edit-company-${c.company_id}`}><Pencil className="w-4 h-4 mr-1" />Editar</Button>
                      )}
                      <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); openChargeModal(c); }} data-testid={`charge-company-${c.company_id}`}>
                        <CreditCard className="w-4 h-4 mr-1" />Cobrar Plano
                      </Button>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); toggleStatus(c); }} data-testid={`toggle-company-${c.company_id}`}
                        className={c.is_active !== false ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}>
                        {c.is_active !== false ? <><UserX className="w-4 h-4 mr-1" />Inativar</> : <><UserCheck className="w-4 h-4 mr-1" />Ativar</>}
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteCompany(c.company_id); }} data-testid={`delete-company-${c.company_id}`}>
                        <Trash2 className="w-4 h-4 mr-1" />Excluir
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {chargeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setChargeModal(null)} data-testid="charge-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Cobrar Plano</h3>
                <p className="text-sm text-slate-500">{chargeModal.company.name}</p>
              </div>
              <button onClick={() => setChargeModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>

            {!chargeModal.url ? (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Plano</Label>
                  <select
                    data-testid="charge-plan-select"
                    value={chargeModal.plan_id}
                    onChange={(e) => setChargeModal(prev => ({ ...prev, plan_id: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                    <option value="annual">Plano Anual (12 meses)</option>
                    <option value="biennial">Plano 24 Meses (10% desc.)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Qtd. Colaboradores</Label>
                  <Input
                    type="number"
                    min={1}
                    data-testid="charge-employee-count"
                    value={chargeModal.employee_count}
                    onChange={(e) => setChargeModal(prev => ({ ...prev, employee_count: e.target.value }))}
                  />
                </div>

                {Number(chargeModal.employee_count) > 100 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">Preço por colaborador (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      data-testid="charge-custom-price"
                      value={chargeModal.custom_price}
                      onChange={(e) => setChargeModal(prev => ({ ...prev, custom_price: e.target.value }))}
                      placeholder="Ex: 8.50"
                    />
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  O link será enviado à empresa (<strong>{chargeModal.company.admin_email || '—'}</strong>). Ao clicar, ela será redirecionada ao Stripe Checkout para efetuar o pagamento.
                </div>

                <Button
                  onClick={generateChargeLink}
                  disabled={chargeModal.generating || !chargeModal.employee_count}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="charge-generate-btn"
                >
                  {chargeModal.generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando...</> : <><CreditCard className="w-4 h-4 mr-2" />Gerar Link de Pagamento</>}
                </Button>
              </>
            ) : (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2" data-testid="charge-result">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                    <Check className="w-5 h-5" /> Link gerado — Total: R$ {(chargeModal.amount || 0).toFixed(2).replace('.', ',')}
                  </div>
                  <p className="text-xs text-emerald-700">Envie este link à empresa via WhatsApp, e-mail ou canal preferido:</p>
                </div>

                <div className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs font-mono break-all" data-testid="charge-url-display">
                  {chargeModal.url}
                </div>

                <div className="flex gap-2">
                  <Button onClick={copyChargeUrl} variant="outline" className="flex-1" data-testid="charge-copy-btn">
                    {chargeModal.copied ? <><Check className="w-4 h-4 mr-2 text-emerald-500" />Copiado!</> : <><Paperclip className="w-4 h-4 mr-2" />Copiar Link</>}
                  </Button>
                  <Button onClick={() => window.open(chargeModal.url, '_blank')} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                    Abrir Checkout
                  </Button>
                </div>

                <Button variant="ghost" size="sm" onClick={() => setChargeModal(null)} className="w-full text-slate-500">
                  Fechar
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== GYMS ====================
// Catálogo de todos os planos suportados (17 planos: Regulares + Extras + Personal + Diárias + Premium)
const ALL_STUDENT_PLANS = [
  { id: 'basic', name: 'Vital Fit', group: 'Regulares', price: 89.90 },
  { id: 'vital_power', name: 'Vital Power', group: 'Regulares', price: 99.90 },
  { id: 'vital_elite', name: 'Vital Elite', group: 'Regulares', price: 109.90 },
  { id: 'plus', name: 'Vital Plus', group: 'Regulares', price: 129.90 },
  { id: 'premium', name: 'Vital Premium', group: 'Regulares', price: 149.90 },
  { id: 'gold', name: 'Vital Gold', group: 'Regulares', price: 169.90 },
  { id: 'vital_infinite', name: 'Vital Infinite', group: 'Regulares', price: 189.90 },
  { id: 'vital_platinum', name: 'Vital Platinum Ilimitado', group: 'Regulares', price: 199.00 },
  { id: 'platinum', name: 'Vital Platinum', group: 'Regulares', price: 199.90 },
  { id: 'action', name: 'Vital Action', group: 'Regulares', price: 279.90 },
  { id: 'vital_start', name: 'Vital Start (7 diárias)', group: 'Diárias', price: 39.90 },
  { id: 'vital_move', name: 'Vital Move (10 diárias)', group: 'Diárias', price: 59.90 },
  { id: 'vital_session', name: 'Vital Session (12 diárias)', group: 'Diárias', price: 69.90 },
  { id: 'vital_ultra', name: 'Vital Ultra Premium', group: 'Premium', price: 239.90 },
  { id: 'vital_action', name: 'Vital Action Premium', group: 'Premium', price: 279.90 },
  { id: 'personal_plus', name: 'Personal Plus', group: 'Personal', price: 469.90 },
  { id: 'personal_gold', name: 'Personal Gold', group: 'Personal', price: 579.90 },
  { id: 'personal_premium', name: 'Personal Premium', group: 'Personal', price: 699.90 },
  { id: 'personal_platinum', name: 'Personal Platinum', group: 'Personal', price: 890.90 },
];

const AcceptedPlansManager = ({ gymId, gymName, initialAccepted, onSaved }) => {
  const [selected, setSelected] = useState(new Set(initialAccepted || []));
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setSelected(new Set(initialAccepted || []));
  }, [initialAccepted]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectGroup = (group) => {
    const next = new Set(selected);
    ALL_STUDENT_PLANS.filter(p => p.group === group).forEach(p => next.add(p.id));
    setSelected(next);
  };
  const clearGroup = (group) => {
    const next = new Set(selected);
    ALL_STUDENT_PLANS.filter(p => p.group === group).forEach(p => next.delete(p.id));
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(ALL_STUDENT_PLANS.map(p => p.id)));
  const clearAll = () => setSelected(new Set());

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios.patch(`${API}/admin/gyms/${gymId}/accepted-plans`, {
        accepted_plans: Array.from(selected),
      });
      toast.success(`Planos atualizados para ${gymName}`);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar planos');
    } finally {
      setSaving(false);
    }
  };

  const groups = ['Regulares', 'Diárias', 'Premium', 'Personal'];
  return (
    <div className="bg-white rounded-xl border-2 border-orange-100 p-4 space-y-3" data-testid={`accepted-plans-${gymId}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        className="w-full flex items-center justify-between text-left"
        data-testid={`accepted-plans-toggle-${gymId}`}
      >
        <div>
          <p className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-orange-600" />
            Planos aceitos ({selected.size}/{ALL_STUDENT_PLANS.length})
          </p>
          <p className="text-xs text-slate-500 mt-0.5">Controla quais planos o aluno pode usar aqui</p>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="text-xs" onClick={(e) => { e.stopPropagation(); selectAll(); }} data-testid={`plans-select-all-${gymId}`}>Selecionar todos</Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={(e) => { e.stopPropagation(); clearAll(); }}>Limpar todos</Button>
          </div>

          {groups.map(group => {
            const plansInGroup = ALL_STUDENT_PLANS.filter(p => p.group === group);
            const selectedInGroup = plansInGroup.filter(p => selected.has(p.id)).length;
            return (
              <div key={group} className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] uppercase font-bold tracking-wide text-slate-500">
                    {group} <span className="text-slate-400">({selectedInGroup}/{plansInGroup.length})</span>
                  </p>
                  <div className="flex gap-1">
                    <button className="text-[10px] text-emerald-600 hover:underline" onClick={(e) => { e.stopPropagation(); selectGroup(group); }}>Todos</button>
                    <span className="text-slate-300">|</span>
                    <button className="text-[10px] text-slate-500 hover:underline" onClick={(e) => { e.stopPropagation(); clearGroup(group); }}>Nenhum</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {plansInGroup.map(p => {
                    const on = selected.has(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={(e) => { e.stopPropagation(); toggle(p.id); }}
                        data-testid={`plan-toggle-${gymId}-${p.id}`}
                        className={`text-[11px] px-2.5 py-1 rounded-full border transition-all font-medium ${
                          on
                            ? 'bg-orange-500 border-orange-500 text-white shadow-sm'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300 hover:bg-orange-50'
                        }`}
                      >
                        {on && <Check className="w-2.5 h-2.5 inline mr-1" />}
                        {p.name} <span className={on ? 'opacity-70' : 'text-slate-400'}>R${p.price.toFixed(0)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            <Button size="sm" onClick={(e) => { e.stopPropagation(); save(); }} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid={`save-accepted-plans-${gymId}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Salvar planos aceitos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminGyms = () => {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const fetchData = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/gyms`).then(r => { setGyms(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleStatus = async (gym) => {
    const newStatus = !(gym.is_active !== false);
    try { await adminAxios.put(`${API}/admin/gyms/${gym.gym_id}/status`, { is_active: newStatus }); toast.success(newStatus ? 'Ativada' : 'Inativada'); fetchData(); } catch { toast.error('Erro'); }
  };
  const deleteGym = async (gymId) => {
    if (!window.confirm('Excluir esta academia e todos os dados?')) return;
    try { await adminAxios.delete(`${API}/admin/gyms/${gymId}`); toast.success('Academia excluída'); fetchData(); } catch { toast.error('Erro ao excluir'); }
  };
  const startEdit = (g) => { setEditing(g.gym_id); setEditData({ cnpj: g.cnpj||'', razao_social: g.razao_social||'', cep: g.cep||'', street: g.street||'', number: g.number||'', city: g.city||'', phone: g.phone||'', email: g.email||'', rh_name: g.rh_name||'', rh_phone: g.rh_phone||'', rh_email: g.rh_email||'', contract_months: g.contract_months||'' }); };
  const saveEdit = async (gymId) => { try { await adminAxios.put(`${API}/admin/gyms/${gymId}/edit`, editData); toast.success('Academia atualizada'); setEditing(null); fetchData(); } catch { toast.error('Erro ao salvar'); } };

  const [uploadingGymPhoto, setUploadingGymPhoto] = useState(null);
  const handleGymPhotoUpload = async (gymId, file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Selecione uma imagem'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande (máx 5MB)'); return; }
    setUploadingGymPhoto(gymId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await adminAxios.post(`${API}/admin/gyms/${gymId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto da academia atualizada!');
      fetchData();
    } catch { toast.error('Erro ao enviar foto'); }
    finally { setUploadingGymPhoto(null); }
  };

  const filtered = gyms.filter(g => g.name?.toLowerCase().includes(search.toLowerCase()) || g.cnpj?.includes(search) || g.admin_email?.toLowerCase().includes(search.toLowerCase()));
  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Academias Parceiras</h1>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome, CNPJ ou email..." className="pl-10" data-testid="admin-gym-search" /></div>
      {filtered.length === 0 ? <EmptyState message="Nenhuma academia encontrada" /> : (
        <div className="space-y-3">{filtered.map(g => (
          <Card key={g.gym_id} className={`border-slate-100 ${g.is_active === false ? 'opacity-60' : ''}`}><CardContent className="p-0">
            <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(expanded === g.gym_id ? null : g.gym_id)}>
              <div className="flex items-center gap-3">
                {g.photo_path ? (
                  <img src={getImageUrl(g.photo_path)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center"><Dumbbell className="w-5 h-5 text-orange-600" /></div>
                )}
                <div><p className="font-semibold text-slate-900">{g.razao_social || g.name}</p>
                <p className="text-xs text-slate-500">{g.cnpj || 'Sem CNPJ'} | {g.city || '-'} | {g.student_count} alunos</p></div>
              </div>
              <div className="flex items-center gap-2"><StatusBadge status={g.is_active !== false ? 'active' : 'inactive'} />
                {expanded === g.gym_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}</div>
            </div>
            {expanded === g.gym_id && (
              <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-3">
                {editing === g.gym_id ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[['cnpj','CNPJ'],['razao_social','Razão Social'],['cep','CEP'],['street','Rua'],['number','Número'],['city','Cidade'],['phone','Telefone'],['email','Email'],['rh_name','Representante'],['rh_phone','Tel. Representante'],['rh_email','Email Representante'],['contract_months','Meses Contrato']].map(([k,l]) => (
                      <div key={k} className="space-y-1"><Label className="text-xs text-slate-500">{l}</Label>
                      <Input value={editData[k]||''} onChange={e => setEditData({...editData,[k]:e.target.value})} className="h-8 text-sm" /></div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-slate-500">ID:</span> <strong className="text-slate-700">{g.gym_id}</strong></div>
                    <div><span className="text-slate-500">CNPJ:</span> <strong className="text-slate-700">{g.cnpj || '-'}</strong></div>
                    <div><span className="text-slate-500">Razão Social:</span> <strong className="text-slate-700">{g.razao_social || '-'}</strong></div>
                    <div><span className="text-slate-500">CEP:</span> <strong className="text-slate-700">{g.cep || '-'}</strong></div>
                    <div><span className="text-slate-500">Rua:</span> <strong className="text-slate-700">{g.street ? `${g.street}, ${g.number || ''}` : '-'}</strong></div>
                    <div><span className="text-slate-500">Cidade:</span> <strong className="text-slate-700">{g.city || '-'}</strong></div>
                    <div><span className="text-slate-500">Telefone:</span> <strong className="text-slate-700">{g.phone || '-'}</strong></div>
                    <div><span className="text-slate-500">Email:</span> <strong className="text-slate-700">{g.email || '-'}</strong></div>
                    <div><span className="text-slate-500">Representante:</span> <strong className="text-slate-700">{g.rh_name || '-'}</strong></div>
                    <div><span className="text-slate-500">Tel. Representante:</span> <strong className="text-slate-700">{g.rh_phone || '-'}</strong></div>
                    <div><span className="text-slate-500">Email Representante:</span> <strong className="text-slate-700">{g.rh_email || '-'}</strong></div>
                    <div><span className="text-slate-500">Contrato:</span> <strong className="text-slate-700">{g.contract_months ? `${g.contract_months} meses` : '-'}</strong></div>
                    <div><span className="text-slate-500">Planos aceitos:</span> <strong className="text-slate-700">{g.accepted_plans?.length ? `${g.accepted_plans.length} planos` : 'Nenhum'}</strong></div>
                    <div><span className="text-slate-500">Alunos:</span> <strong className="text-slate-700">{g.student_count}</strong></div>
                    <div><span className="text-slate-500">Admin:</span> <strong className="text-slate-700">{g.admin_name || '-'}</strong></div>
                    <div><span className="text-slate-500">Criado em:</span> <strong className="text-slate-700">{g.created_at ? new Date(g.created_at).toLocaleDateString('pt-BR') : '-'}</strong></div>
                  </div>
                )}

                {/* Accepted Plans Manager */}
                <AcceptedPlansManager
                  gymId={g.gym_id}
                  gymName={g.name}
                  initialAccepted={g.accepted_plans || []}
                  onSaved={fetchData}
                />
                <div className="flex gap-2 flex-wrap">
                  {/* Photo Upload */}
                  <div className="flex items-center gap-3 mr-auto bg-white rounded-lg border border-slate-200 px-3 py-2">
                    {g.photo_path ? (
                      <img src={getImageUrl(g.photo_path)} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                        <Camera className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleGymPhotoUpload(g.gym_id, e.target.files[0])} data-testid={`gym-photo-upload-${g.gym_id}`} />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors">
                        {uploadingGymPhoto === g.gym_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                        {g.photo_path ? 'Trocar Foto' : 'Adicionar Foto'}
                      </span>
                    </label>
                  </div>
                  {editing === g.gym_id ? (
                    <><Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); saveEdit(g.gym_id); }}><Save className="w-4 h-4 mr-1" />Salvar</Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(null); }}><X className="w-4 h-4 mr-1" />Cancelar</Button></>
                  ) : (
                    <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); startEdit(g); }} data-testid={`edit-gym-${g.gym_id}`}><Pencil className="w-4 h-4 mr-1" />Editar</Button>
                  )}
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); toggleStatus(g); }} data-testid={`toggle-gym-${g.gym_id}`}
                    className={g.is_active !== false ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}>
                    {g.is_active !== false ? <><UserX className="w-4 h-4 mr-1" />Inativar</> : <><UserCheck className="w-4 h-4 mr-1" />Ativar</>}
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteGym(g.gym_id); }} data-testid={`delete-gym-${g.gym_id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />Excluir
                  </Button>
                </div>
              </div>
            )}
          </CardContent></Card>
        ))}</div>
      )}
    </div>
  );
};

// ==================== STUDENTS ====================
const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [discountInputs, setDiscountInputs] = useState({});

  const fetchData = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/students`, { params: { filter, search, page, per_page: 20 } })
      .then(r => {
        setStudents(r.data.students || []);
        setTotalPages(r.data.total_pages || 1);
        setTotal(r.data.total || 0);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [filter, search, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = () => { setSearch(searchInput); setPage(1); };

  const approveStudent = async (studentId) => {
    try {
      await adminAxios.put(`${API}/admin/students/${studentId}/approve`);
      toast.success('Aluno liberado com sucesso!');
      fetchData();
    } catch { toast.error('Erro ao liberar aluno'); }
  };

  const toggleStatus = async (student) => {
    const newStatus = !student.is_active;
    try { await adminAxios.put(`${API}/admin/students/${student.student_id}/status`, { is_active: newStatus }); toast.success(newStatus ? 'Ativado' : 'Inativado'); fetchData(); } catch { toast.error('Erro'); }
  };
  const deleteStudent = async (studentId) => {
    if (!window.confirm('Excluir este aluno permanentemente?')) return;
    try { await adminAxios.delete(`${API}/admin/students/${studentId}`); toast.success('Aluno excluído'); fetchData(); } catch { toast.error('Erro ao excluir'); }
  };
  const startEdit = (s) => { setEditing(s.student_id); setEditData({ name: s.name||'', email: s.email||'', cpf: s.cpf||'', phone: s.phone||'', cellphone: s.cellphone||'', department: s.department||'', plan: s.plan||'' }); };
  const saveEdit = async (studentId) => { try { await adminAxios.put(`${API}/admin/students/${studentId}/edit`, editData); toast.success('Aluno atualizado'); setEditing(null); fetchData(); } catch { toast.error('Erro ao salvar'); } };

  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const handleAdminPhotoUpload = async (studentId, file) => {
    if (!file || !file.type.startsWith('image/')) { toast.error('Selecione uma imagem'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande (máx 5MB)'); return; }
    setUploadingPhoto(studentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await adminAxios.post(`${API}/admin/students/${studentId}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto atualizada!');
      fetchData();
    } catch { toast.error('Erro ao enviar foto'); }
    finally { setUploadingPhoto(null); }
  };

  const setDiscount = async (studentId, amount, active) => {
    try {
      await adminAxios.put(`${API}/admin/students/${studentId}/discount`, {
        discount_amount: parseFloat(amount) || 0,
        discount_active: active
      });
      toast.success(active ? `Desconto de R$ ${parseFloat(amount).toFixed(2)} ativado!` : 'Desconto desativado');
      fetchData();
    } catch { toast.error('Erro ao configurar desconto'); }
  };

  const getDiscountInput = (s) => {
    if (discountInputs[s.student_id] !== undefined) return discountInputs[s.student_id];
    return s.discount_amount || '';
  };

  const planLabel = (p) => ({ basic: 'Fit', plus: 'Plus', premium: 'Premium', gold: 'Gold', platinum: 'Platinum', action: 'Action', personal_standart: 'P. Standart', personal_plus: 'P. Plus', personal_gold: 'P. Gold', personal_premium: 'P. Premium', personal_platinum: 'P. Platinum' }[p] || 'Sem plano');

  const filters = [
    { id: 'all', label: 'Todos' },
    { id: 'pending', label: 'Pendentes' },
    { id: 'active', label: 'Ativos' },
    { id: 'last_30', label: 'Últimos 30 dias' },
    { id: 'last_5', label: 'Últimos 5 dias' },
  ];

  const getApprovalBadge = (s) => {
    const status = s.approval_status || 'approved';
    if (status === 'pending') return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pendente</span>;
    return <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Liberado</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-students-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Alunos</h1>
          <p className="text-sm text-slate-500 mt-1">{total} aluno(s) encontrado(s)</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="student-filters">
        {filters.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
            data-testid={`filter-${f.id}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.id ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Buscar por nome..." className="pl-10" data-testid="admin-student-search" />
        </div>
        <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="search-btn">Buscar</Button>
      </div>

      {loading ? <LoadingSpinner /> : students.length === 0 ? <EmptyState message="Nenhum aluno encontrado" /> : (
        <>
          <div className="space-y-2">
            {students.map(s => {
              const isPending = (s.approval_status || 'approved') === 'pending';
              const hasDiscount = s.discount_active && s.discount_amount > 0;
              return (
              <Card key={s.student_id} className={`border-slate-100 ${isPending ? 'border-l-4 border-l-amber-400' : ''} ${!s.is_active && !isPending ? 'opacity-60' : ''}`} data-testid={`student-card-${s.student_id}`}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(expanded === s.student_id ? null : s.student_id)}>
                    <div className="flex items-center gap-3">
                      {s.profile_photo ? (
                        <img src={getImageUrl(s.profile_photo)} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isPending ? 'bg-amber-50' : s.is_active ? 'bg-emerald-50' : 'bg-slate-100'}`}>
                          <Users className={`w-5 h-5 ${isPending ? 'text-amber-600' : s.is_active ? 'text-emerald-600' : 'text-slate-400'}`} />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{s.name}</p>
                          {hasDiscount && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid={`discount-badge-${s.student_id}`}>
                              <Tag className="w-3 h-3" /> R$ {s.discount_amount.toFixed(2)} OFF
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">{s.company_name} | {s.gym_name} | {planLabel(s.plan)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 hidden md:block">{s.checkins_30d} check-ins/30d</span>
                      {getApprovalBadge(s)}
                      {expanded === s.student_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>
                  {expanded === s.student_id && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-4">
                      {editing === s.student_id ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[['name','Nome'],['email','Email'],['cpf','CPF'],['phone','Telefone'],['cellphone','Celular'],['department','Departamento'],['plan','Plano']].map(([k,l]) => (
                            <div key={k} className="space-y-1"><Label className="text-xs text-slate-500">{l}</Label>
                            <Input value={editData[k]||''} onChange={e => setEditData({...editData,[k]:e.target.value})} className="h-8 text-sm" /></div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div><span className="text-slate-500">CPF:</span> <strong className="text-slate-700">{s.cpf || '-'}</strong></div>
                          <div><span className="text-slate-500">Telefone:</span> <strong className="text-slate-700">{s.phone || '-'}</strong></div>
                          <div><span className="text-slate-500">Celular:</span> <strong className="text-slate-700">{s.cellphone || '-'}</strong></div>
                          <div><span className="text-slate-500">Email:</span> <strong className="text-slate-700">{s.email || '-'}</strong></div>
                          <div><span className="text-slate-500">Empresa:</span> <strong className="text-slate-700">{s.company_name}</strong></div>
                          <div><span className="text-slate-500">Academia Vínculo:</span> <strong className="text-slate-700">{s.gym_name}</strong></div>
                          <div><span className="text-slate-500">Plano:</span> <strong className="text-slate-700">{planLabel(s.plan)}</strong></div>
                          <div><span className="text-slate-500">Código:</span> <strong className="font-mono text-slate-700">{s.access_code}</strong></div>
                          <div><span className="text-slate-500">Check-ins (30d):</span> <strong className="text-slate-700">{s.checkins_30d}</strong></div>
                          <div><span className="text-slate-500">Extras usados:</span> <strong className="text-slate-700">{s.extra_checkins_used || 0}/{s.extra_checkins_limit || 0}</strong></div>
                          <div><span className="text-slate-500">Departamento:</span> <strong className="text-slate-700">{s.department || '-'}</strong></div>
                          <div><span className="text-slate-500">Criado em:</span> <strong className="text-slate-700">{s.created_at ? new Date(s.created_at).toLocaleDateString('pt-BR') : '-'}</strong></div>
                        </div>
                      )}

                      {/* Photo Upload */}
                      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`photo-section-${s.student_id}`}>
                        <div className="flex items-center gap-4">
                          <div className="relative group">
                            {s.profile_photo ? (
                              <img src={getImageUrl(s.profile_photo)} alt="" className="w-16 h-16 rounded-xl object-cover border-2 border-slate-200" />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                                <Camera className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-800">Foto do Aluno</p>
                            <p className="text-[11px] text-slate-400 mb-2">JPG/PNG, máx 5MB</p>
                            <label className="cursor-pointer">
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleAdminPhotoUpload(s.student_id, e.target.files[0])} data-testid={`photo-upload-${s.student_id}`} />
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                                {uploadingPhoto === s.student_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                {s.profile_photo ? 'Trocar Foto' : 'Enviar Foto'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Discount Section */}
                      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`discount-section-${s.student_id}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <DollarSign className="w-4 h-4 text-violet-600" />
                          <span className="text-sm font-semibold text-slate-800">Sistema de Desconto</span>
                          {hasDiscount && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">ATIVO</span>}
                        </div>
                        <div className="flex items-end gap-3 flex-wrap">
                          <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Total de Desconto (R$)</Label>
                            <Input
                              type="number" min="0" step="0.01"
                              data-testid={`discount-input-${s.student_id}`}
                              value={getDiscountInput(s)}
                              onChange={e => setDiscountInputs(prev => ({ ...prev, [s.student_id]: e.target.value }))}
                              placeholder="0.00"
                              className="w-36 h-9 text-sm"
                              onClick={e => e.stopPropagation()}
                            />
                          </div>
                          <Button
                            size="sm"
                            data-testid={`activate-discount-${s.student_id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const val = getDiscountInput(s);
                              setDiscount(s.student_id, val, true);
                            }}
                            className="bg-violet-600 hover:bg-violet-700 text-white h-9"
                          >
                            <Tag className="w-3.5 h-3.5 mr-1.5" />Ativar Desconto
                          </Button>
                          {hasDiscount && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`deactivate-discount-${s.student_id}`}
                              onClick={(e) => { e.stopPropagation(); setDiscount(s.student_id, s.discount_amount, false); }}
                              className="text-red-600 border-red-200 hover:bg-red-50 h-9"
                            >
                              <X className="w-3.5 h-3.5 mr-1.5" />Remover
                            </Button>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-400 mt-2">
                          Aplica-se aos planos: Vital Fit, Plus, Premium, Gold, Platinum e Action
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        {isPending && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={(e) => { e.stopPropagation(); approveStudent(s.student_id); }} data-testid={`approve-student-${s.student_id}`}>
                            <ShieldCheck className="w-4 h-4 mr-1" />Liberar Aluno
                          </Button>
                        )}
                        {editing === s.student_id ? (
                          <><Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={(e) => { e.stopPropagation(); saveEdit(s.student_id); }}><Save className="w-4 h-4 mr-1" />Salvar</Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(null); }}><X className="w-4 h-4 mr-1" />Cancelar</Button></>
                        ) : (
                          <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50" onClick={(e) => { e.stopPropagation(); startEdit(s); }} data-testid={`edit-student-${s.student_id}`}><Pencil className="w-4 h-4 mr-1" />Editar</Button>
                        )}
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); toggleStatus(s); }} data-testid={`toggle-student-${s.student_id}`}
                          className={s.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}>
                          {s.is_active ? <><UserX className="w-4 h-4 mr-1" />Inativar</> : <><UserCheck className="w-4 h-4 mr-1" />Ativar</>}
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteStudent(s.student_id); }} data-testid={`delete-student-${s.student_id}`}>
                          <Trash2 className="w-4 h-4 mr-1" />Excluir
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4" data-testid="students-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
              <span className="text-sm text-slate-600">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ==================== NOTIFICATIONS ====================
const AdminNotifications = () => {
  const [contacts, setContacts] = useState({ companies: [], gyms: [], students: [] });
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('companies');
  const [searchTerm, setSearchTerm] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  const fetchHistory = useCallback((pg = 1) => {
    setHistoryLoading(true);
    adminAxios.get(`${API}/admin/notifications-history`, { params: { page: pg, per_page: 10 } })
      .then(r => {
        setHistory(r.data.history || []);
        setHistoryTotalPages(r.data.total_pages || 1);
        setHistoryTotal(r.data.total || 0);
        setHistoryLoading(false);
      }).catch(() => setHistoryLoading(false));
  }, []);

  useEffect(() => {
    adminAxios.get(`${API}/admin/contacts-list`).then(r => { setContacts(r.data); setLoading(false); }).catch(() => setLoading(false));
    fetchHistory(1);
  }, [fetchHistory]);

  const currentList = (contacts[tab] || []).filter(c =>
    !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleContact = (contact) => {
    if (contact.email) {
      setSelectedEmails(prev => prev.includes(contact.email) ? prev.filter(e => e !== contact.email) : [...prev, contact.email]);
    }
    if (contact.phone) {
      const key = `${contact.phone}|${contact.name}`;
      setSelectedPhones(prev => prev.some(p => p.key === key) ? prev.filter(p => p.key !== key) : [...prev, { key, phone: contact.phone, name: contact.name }]);
    }
  };

  const isSelected = (contact) => selectedEmails.includes(contact.email);

  const selectAll = () => {
    const emails = currentList.map(c => c.email).filter(Boolean);
    const allSelected = emails.every(e => selectedEmails.includes(e));
    if (allSelected) {
      setSelectedEmails(prev => prev.filter(e => !emails.includes(e)));
      const phoneKeys = currentList.filter(c => c.phone).map(c => `${c.phone}|${c.name}`);
      setSelectedPhones(prev => prev.filter(p => !phoneKeys.includes(p.key)));
    } else {
      setSelectedEmails(prev => [...new Set([...prev, ...emails])]);
      const newPhones = currentList.filter(c => c.phone).map(c => ({ key: `${c.phone}|${c.name}`, phone: c.phone, name: c.name }));
      setSelectedPhones(prev => {
        const existing = new Set(prev.map(p => p.key));
        return [...prev, ...newPhones.filter(p => !existing.has(p.key))];
      });
    }
  };

  const handleSendEmail = async () => {
    if (!selectedEmails.length) { toast.error('Selecione pelo menos um destinatário'); return; }
    if (!message.trim()) { toast.error('Digite uma mensagem'); return; }
    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('recipients', JSON.stringify(selectedEmails));
      formData.append('subject', subject || 'Aviso Vital Performance');
      formData.append('message', message.trim());
      formData.append('channel', 'email');
      if (attachment) formData.append('attachment', attachment);

      const res = await adminAxios.post(`${API}/admin/send-notification`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      if (res.data.errors > 0) toast.warning(`${res.data.errors} e-mail(s) falharam`);
      setMessage(''); setSubject(''); setSelectedEmails([]); setSelectedPhones([]); setAttachment(null);
      fetchHistory(1); setHistoryPage(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar e-mails'); }
    finally { setSendingEmail(false); }
  };

  const handleSendWhatsapp = async () => {
    const phonesWithData = selectedPhones.filter(p => p.phone);
    if (!phonesWithData.length) { toast.error('Nenhum destinatário com telefone cadastrado'); return; }
    if (!message.trim()) { toast.error('Digite uma mensagem'); return; }
    setSendingWhatsapp(true);
    try {
      const res = await adminAxios.post(`${API}/admin/send-whatsapp-notification`, {
        recipients: phonesWithData.map(p => ({ phone: p.phone, name: p.name })),
        message: message.trim()
      });
      toast.success(res.data.message);
      if (res.data.errors > 0) toast.warning(`${res.data.errors} mensagem(ns) falharam`);
      setMessage(''); setSubject(''); setSelectedEmails([]); setSelectedPhones([]);
      fetchHistory(1); setHistoryPage(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar WhatsApp'); }
    finally { setSendingWhatsapp(false); }
  };

  const tabs = [
    { id: 'companies', label: 'Empresas', count: contacts.companies?.length || 0 },
    { id: 'gyms', label: 'Academias', count: contacts.gyms?.length || 0 },
    { id: 'students', label: 'Alunos', count: contacts.students?.length || 0 },
  ];

  return (
    <div className="space-y-6" data-testid="admin-notifications-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Painel de Avisos</h1>
          <p className="text-sm text-slate-500 mt-1">Envie comunicados por E-mail e WhatsApp</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="bg-blue-50 text-blue-600 px-2 py-1 rounded-full font-medium">{selectedEmails.length} e-mail(s)</span>
          <span className="bg-emerald-50 text-emerald-600 px-2 py-1 rounded-full font-medium">{selectedPhones.filter(p => p.phone).length} telefone(s)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Recipients */}
        <Card className="border-slate-100">
          <CardContent className="p-0">
            <div className="flex border-b border-slate-100" data-testid="notification-tabs">
              {tabs.map(t => (
                <button key={t.id} onClick={() => { setTab(t.id); setSearchTerm(''); }}
                  data-testid={`tab-${t.id}`}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${tab === t.id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
                  {t.label} ({t.count})
                </button>
              ))}
            </div>
            <div className="p-3 border-b border-slate-100 space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox checked={currentList.length > 0 && currentList.every(c => selectedEmails.includes(c.email))}
                  onCheckedChange={selectAll} data-testid="select-all-checkbox" />
                <span className="text-xs text-slate-500">Selecionar todos</span>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar contato..." className="pl-8 h-8 text-xs" data-testid="contacts-search" />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {loading ? <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div> :
                currentList.length === 0 ? <p className="p-4 text-center text-sm text-slate-400">Nenhum contato</p> :
                currentList.map((c, i) => (
                  <div key={c.id || c.email || i} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b border-slate-50" data-testid={`contact-row-${i}`}>
                    <Checkbox checked={isSelected(c)}
                      onCheckedChange={() => toggleContact(c)} data-testid={`check-${c.id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span className="truncate">{c.email}</span>
                        {c.phone && <span className="flex items-center gap-0.5 shrink-0"><Phone className="w-3 h-3" />{c.phone}</span>}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
          </CardContent>
        </Card>

        {/* Right: Compose */}
        <Card className="border-slate-100">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-bold text-slate-900">Compor Aviso</h3>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Assunto do aviso" data-testid="notification-subject" />
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Digite a mensagem do aviso..." rows={6}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
              data-testid="notification-message" />

            {/* Attachment */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Anexo (PDF, JPG, PNG - máx. 10MB)</Label>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-sm text-slate-600">
                  <Paperclip className="w-4 h-4" />
                  {attachment ? attachment.name : 'Selecionar arquivo'}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                    onChange={e => setAttachment(e.target.files[0] || null)}
                    data-testid="notification-attachment" />
                </label>
                {attachment && (
                  <Button variant="ghost" size="sm" onClick={() => setAttachment(null)} className="text-red-500 hover:text-red-700 h-8 px-2">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-2">
              <Button onClick={handleSendEmail} disabled={sendingEmail || !selectedEmails.length || !message.trim()}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-email-btn">
                {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar por E-mail ({selectedEmails.length})
              </Button>
              <Button onClick={handleSendWhatsapp} disabled={sendingWhatsapp || !selectedPhones.filter(p => p.phone).length || !message.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-whatsapp-btn">
                {sendingWhatsapp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                Enviar por WhatsApp ({selectedPhones.filter(p => p.phone).length})
              </Button>
            </div>
            <p className="text-[11px] text-slate-400">E-mail remetente: contato@vitalperformance.app.br | WhatsApp via Twilio Sandbox</p>
          </CardContent>
        </Card>
      </div>

      {/* History Section */}
      <Card className="border-slate-100" data-testid="notifications-history-section">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Histórico de Avisos</h3>
              <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{historyTotal}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => { fetchHistory(historyPage); }} className="gap-1.5 h-8">
              <RefreshCw className="w-3.5 h-3.5" />Atualizar
            </Button>
          </div>
          {historyLoading ? (
            <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : history.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">Nenhum aviso enviado ainda</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="notifications-history-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Canal</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Assunto / Mensagem</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Destinatários</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Enviados</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Anexo</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Data/Hora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const errCount = h.errors?.length || h.errors_count || 0;
                      return (
                        <tr key={h.id || h.created_at || i} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`history-row-${i}`}>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${h.channel === 'whatsapp' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {h.channel === 'whatsapp' ? 'WhatsApp' : 'E-mail'}
                            </span>
                          </td>
                          <td className="px-4 py-3 max-w-xs">
                            {h.subject && <p className="text-sm font-medium text-slate-800 truncate">{h.subject}</p>}
                            <p className="text-xs text-slate-500 truncate">{h.message?.substring(0, 80)}{h.message?.length > 80 ? '...' : ''}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{h.recipients_count || 0}</td>
                          <td className="px-4 py-3">

// ==================== COPARTICIPATIONS ADMIN ====================
                            <span className="text-sm font-medium text-emerald-600">{h.sent_count || 0}</span>
                            {errCount > 0 && <span className="text-xs text-red-500 ml-1">({errCount} falha{errCount > 1 ? 's' : ''})</span>}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {h.has_attachment ? (
                              <span className="flex items-center gap-1 text-blue-600"><Paperclip className="w-3 h-3" />{h.attachment_name || 'Sim'}</span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {h.timestamp ? new Date(h.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {historyTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-100" data-testid="history-pagination">
                  <Button variant="outline" size="sm" disabled={historyPage <= 1}
                    onClick={() => { const p = historyPage - 1; setHistoryPage(p); fetchHistory(p); }}>Anterior</Button>
                  <span className="text-xs text-slate-500">Página {historyPage} de {historyTotalPages}</span>
                  <Button variant="outline" size="sm" disabled={historyPage >= historyTotalPages}
                    onClick={() => { const p = historyPage + 1; setHistoryPage(p); fetchHistory(p); }}>Próxima</Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
const AdminCoparticipations = () => {
  const [copars, setCopars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [additionalValues, setAdditionalValues] = useState({});

  const fetchData = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/coparticipations`).then(r => { setCopars(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const activate = async (coparId) => {
    const val = additionalValues[coparId] || 0;
    try {
      const res = await adminAxios.put(`${API}/admin/coparticipations/${coparId}/activate`, { additional_value: parseFloat(val) || 0 });
      toast.success(res.data.message || 'Coparticipação ativada!');
      fetchData();
    } catch { toast.error('Erro ao ativar'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-coparticipations-section">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Coparticipações</h1>
        <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>
      {loading ? <LoadingSpinner /> : copars.length === 0 ? <EmptyState message="Nenhuma coparticipação pendente" /> : (
        <div className="space-y-3">
          {copars.map(c => (
            <Card key={c.copar_id} className={`border-slate-100 ${c.status === 'pending' ? 'border-l-4 border-l-amber-400' : c.status === 'active' ? 'border-l-4 border-l-emerald-500' : ''}`} data-testid={`admin-copar-${c.copar_id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-slate-900">{c.name} <span className="text-xs text-slate-400">(titular: {c.student_name})</span></p>
                    <p className="text-xs text-slate-500">CPF: {c.cpf} | {c.email || '-'} | {c.cellphone || '-'} | Plano: {c.student_plan || '-'}</p>
                    <p className="text-xs text-slate-400">Cidade: {c.city || '-'} | Bairro: {c.neighborhood || '-'} | Rua: {c.street || '-'}, {c.number || '-'}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {c.status === 'active' ? 'Ativa' : 'Pendente'}
                  </span>
                </div>
                {c.status === 'active' && c.access_code && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`admin-copar-active-${c.copar_id}`}>
                    <div>
                      <p className="text-xs font-semibold text-emerald-700">Código de Acesso Gerado</p>
                      <p className="text-xl font-black text-emerald-800 tracking-widest font-mono">{c.access_code}</p>
                    </div>
                    <div className="text-right text-xs text-emerald-600">
                      <p>Valor: R$ {(c.additional_value || 0).toFixed(2).replace('.', ',')}</p>
                      {c.activated_at && <p>Ativado em: {new Date(c.activated_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>}
                    </div>
                  </div>
                )}
                {c.status === 'pending' && (
                  <div className="flex items-end gap-3 bg-slate-50 p-3 rounded-lg">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Valor Adicional (R$)</Label>
                      <Input type="number" min="0" step="0.01"
                        value={additionalValues[c.copar_id] ?? c.suggested_additional ?? ''}
                        onChange={e => setAdditionalValues(prev => ({...prev, [c.copar_id]: e.target.value}))}
                        className="w-36 h-9 text-sm" data-testid={`copar-value-${c.copar_id}`} />
                      <p className="text-[10px] text-slate-400">Sugerido: R$ {(c.suggested_additional || 0).toFixed(2)}</p>
                    </div>
                    <Button size="sm" onClick={() => activate(c.copar_id)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid={`activate-copar-${c.copar_id}`}>
                      <Check className="w-3.5 h-3.5 mr-1.5" />Ativar e Gerar Código
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== CHECKINS ====================
const AdminCheckins = () => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/checkins`, { params: { date, page, per_page: 50 } })
      .then(r => {
        setCheckins(r.data.checkins || []);
        setTotalPages(r.data.total_pages || 1);
        setTotal(r.data.total || 0);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [date, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await adminAxios.get(`${API}/admin/checkins/pdf`, { params: { date }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `checkins_${date || 'todos'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado com sucesso!');
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-checkins-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Registro de Check-ins</h1>
          <p className="text-sm text-slate-500 mt-1">{total} check-in(s) encontrado(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
          <Button onClick={downloadPdf} disabled={downloading} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="download-checkins-pdf">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            Exportar PDF
          </Button>
        </div>
      </div>

      {/* Date Filter */}
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Data</label>
          <Input type="date" value={date} onChange={e => { setDate(e.target.value); setPage(1); }} className="w-48" data-testid="checkin-date-filter" />
        </div>
        <Button variant="outline" onClick={() => { setDate(''); setPage(1); }} data-testid="clear-date-filter">Limpar filtro</Button>
      </div>

      {loading ? <LoadingSpinner /> : checkins.length === 0 ? <EmptyState message="Nenhum check-in encontrado" /> : (
        <>
          <Card className="border-slate-100">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="checkins-table">
                  <thead><tr className="border-b border-slate-100 bg-slate-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Aluno</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Academia</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Empresa</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Data/Hora</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Modalidade</th>
                  </tr></thead>
                  <tbody>
                    {checkins.map((c, i) => (
                      <tr key={c.checkin_id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-900">{c.student_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{c.gym_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{c.company_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {c.timestamp ? new Date(c.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_extra_checkin ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                            {c.is_extra_checkin ? 'Extra' : 'Normal'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{c.modality || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4" data-testid="checkins-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
              <span className="text-sm text-slate-600">Página {page} de {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ==================== PAYMENTS ====================
const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(() => {
    setLoading(true);
    adminAxios.get(`${API}/admin/payments`).then(r => { setPayments(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = payments.filter(p => {
    const matchSearch = p.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.user_email?.toLowerCase().includes(search.toLowerCase()) ||
      p.plan_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || p.payment_status === filter;
    return matchSearch && matchFilter;
  });

  const totals = {
    paid: payments.filter(p => p.payment_status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0),
    pending: payments.filter(p => p.payment_status === 'pending').length,
    total: payments.length
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Pagamentos</h1>
        <Button variant="outline" onClick={fetch} className="gap-2"><RefreshCw className="w-4 h-4" />Atualizar</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-100"><CardContent className="p-4">
          <p className="text-sm text-slate-500 mb-1">Receita Confirmada</p>
          <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Chivo, sans-serif' }}>R$ {totals.paid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-4">
          <p className="text-sm text-slate-500 mb-1">Pendentes</p>
          <p className="text-2xl font-bold text-orange-600" style={{ fontFamily: 'Chivo, sans-serif' }}>{totals.pending}</p>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-4">
          <p className="text-sm text-slate-500 mb-1">Total de Transações</p>
          <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{totals.total}</p>
        </CardContent></Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nome, email ou plano..." className="pl-10" data-testid="admin-payment-search" />
        </div>
        <div className="flex gap-2">
          {['all', 'paid', 'pending', 'unpaid'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm"
              onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={filter === f ? 'bg-slate-900' : ''}>
              {{ all: 'Todos', paid: 'Pagos', pending: 'Pendentes', unpaid: 'Não pagos' }[f]}
            </Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState message="Nenhuma transação encontrada" /> : (
        <Card className="border-slate-100">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-slate-100">
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Usuário</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Plano</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Valor</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Data</th>
              </tr></thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.transaction_id || p.payment_id || p.id || i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-slate-900">{p.user_name}</p>
                      <p className="text-xs text-slate-500">{p.user_email}</p>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-700">{p.plan_name}</td>
                    <td className="px-5 py-3 text-sm text-slate-500 capitalize">{p.plan_type === 'company' ? 'Empresa' : 'Aluno'}</td>
                    <td className="px-5 py-3 text-sm font-medium text-slate-900">R$ {p.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3"><StatusBadge status={p.payment_status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== SHARED COMPONENTS ====================
const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-700',
    active: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-orange-100 text-orange-700',
    unpaid: 'bg-red-100 text-red-700',
    inactive: 'bg-slate-100 text-slate-600',
    open: 'bg-blue-100 text-blue-700',
  };
  const labels = { paid: 'Pago', active: 'Ativo', pending: 'Pendente', unpaid: 'Não pago', inactive: 'Inativo', open: 'Aberto' };
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-16"><AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">{message}</p></div>
);

// Student Registration Requests
const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await adminAxios.get(`${API}/admin/student-requests`);
      setRequests(res.data);
    } catch { toast.error('Erro ao carregar solicitações'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (reqId) => {
    setProcessing(reqId);
    try {
      const res = await adminAxios.put(`${API}/admin/student-requests/${reqId}/approve`);
      toast.success(`Aprovado! Código: ${res.data.access_code}`);
      fetchRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
    finally { setProcessing(null); }
  };

  const handleReject = async (reqId) => {
    if (!window.confirm('Rejeitar esta solicitação?')) return;
    setProcessing(reqId);
    try {
      await adminAxios.put(`${API}/admin/student-requests/${reqId}/reject`);
      toast.success('Solicitação rejeitada');
      fetchRequests();
    } catch { toast.error('Erro ao rejeitar'); }
    finally { setProcessing(null); }
  };

  const pending = requests.filter(r => r.status === 'pending');
  const processed = requests.filter(r => r.status !== 'pending');

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-requests">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Solicitações de Cadastro</h1>
          <p className="text-slate-500 text-sm">Cadastros solicitados pelas academias</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRequests}><RefreshCw className="w-4 h-4 mr-2" />Atualizar</Button>
      </div>

      {/* Pending */}
      <div>
        <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" /> Pendentes ({pending.length})
        </h2>
        {pending.length === 0 ? (
          <Card className="border-slate-100"><CardContent className="p-8 text-center">
            <Check className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
            <p className="text-slate-500">Nenhuma solicitação pendente</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {pending.map(r => (
              <Card key={r.request_id} className="border-slate-100 hover:shadow-sm transition-shadow" data-testid={`request-${r.request_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-slate-900">{r.name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                        {r.email && <span>{r.email}</span>}
                        {r.cellphone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.cellphone}</span>}
                        {r.address && <span>{r.address}</span>}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Academia: <strong>{r.gym_name}</strong> | {new Date(r.created_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApprove(r.request_id)} disabled={processing === r.request_id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${r.request_id}`}>
                        {processing === r.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Aprovar</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(r.request_id)} disabled={processing === r.request_id}
                        className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-${r.request_id}`}>
                        <X className="w-4 h-4 mr-1" />Rejeitar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Processed */}
      {processed.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Processados ({processed.length})</h2>
          <Card className="border-slate-100"><CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="processed-requests-table">
                <thead><tr className="border-b border-slate-100">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nome</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Academia</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Data</th>
                </tr></thead>
                <tbody>
                  {processed.map(r => (
                    <tr key={r.request_id} className="border-b border-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-800">{r.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{r.gym_name}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {r.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                      </span></td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-600">{r.access_code || '-'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
};

// ==================== UNVERIFIED USERS ====================
const UnverifiedUsersSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAxios.get(`${API}/admin/unverified-users`);
      setUsers(res.data.users || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleVerify = async (userId, email) => {
    setVerifying(userId);
    try {
      await adminAxios.post(`${API}/admin/verify-user/${userId}`);
      toast.success(`${email} verificado com sucesso!`);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao verificar');
    } finally {
      setVerifying(null);
    }
  };

  if (loading) return null;
  if (users.length === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50/50" data-testid="unverified-users-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          Usuários Não Verificados ({users.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-amber-700 mb-3">Estes usuários não verificaram o e-mail. Você pode verificá-los manualmente.</p>
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.user_id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{u.name || u.email}</p>
                <p className="text-xs text-slate-500">{u.email} — {u.role === 'gym' ? 'Academia' : u.role === 'rh' ? 'Empresa' : u.role || 'N/A'}</p>
              </div>
              <Button
                size="sm"
                onClick={() => handleVerify(u.user_id, u.email)}
                disabled={verifying === u.user_id}
                data-testid={`verify-user-${u.user_id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white ml-2 flex-shrink-0"
              >
                {verifying === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5 mr-1" />}
                Verificar
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== SECURITY (2FA) ====================
// ==================== EMAIL FIX ====================
const EmailFixSection = () => {
  const [oldEmail, setOldEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  const handleFix = async () => {
    if (!oldEmail.trim() || !newEmail.trim()) { toast.error('Preencha ambos os campos'); return; }
    if (oldEmail.trim() === newEmail.trim()) { toast.error('Os emails sao iguais'); return; }
    setFixing(true);
    setFixResult(null);
    try {
      const res = await adminAxios.post(`${API}/admin/fix-email`, { old_email: oldEmail.trim(), new_email: newEmail.trim() });
      setFixResult({ success: true, message: res.data.message });
      toast.success(res.data.message);
      setOldEmail('');
      setNewEmail('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao corrigir email';
      setFixResult({ success: false, message: detail });
      toast.error(detail);
    } finally {
      setFixing(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="email-fix-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-5 h-5 text-blue-600" />
          Corrigir Email de Usuario
        </CardTitle>
        <p className="text-sm text-slate-500">Corrija erros de digitacao no email de academias e empresas.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {fixResult && (
          <div className={`rounded-xl p-3 text-sm font-medium ${fixResult.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="fix-email-result">
            {fixResult.message}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Email incorreto</Label>
            <Input value={oldEmail} onChange={e => setOldEmail(e.target.value)} placeholder="email.errado@hotmsil.com" data-testid="old-email-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Email correto</Label>
            <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email.correto@hotmail.com" data-testid="new-email-input" />
          </div>
        </div>
        <Button onClick={handleFix} disabled={fixing || !oldEmail.trim() || !newEmail.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="fix-email-btn">
          {fixing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          Corrigir Email
        </Button>
      </CardContent>
    </Card>
  );
};

// ==================== PASSWORD RESET ====================
const PasswordResetSection = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetting, setResetting] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    adminAxios.get(`${API}/admin/users-for-reset`).then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleReset = async (userId) => {
    if (!window.confirm('Tem certeza que deseja resetar a senha deste usuario?')) return;
    setResetting(userId);
    setResult(null);
    try {
      const res = await adminAxios.post(`${API}/admin/reset-password`, { user_id: userId });
      setResult(res.data);
      toast.success('Senha resetada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    } finally {
      setResetting(null);
    }
  };

  const filtered = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.entity_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Card className="border-slate-200" data-testid="password-reset-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="w-5 h-5 text-amber-600" />
          Resetar Senha de Usuarios
        </CardTitle>
        <p className="text-sm text-slate-500">Gere uma nova senha de 8 digitos para academias e empresas. O usuario pode alterar no perfil ao acessar.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Result banner */}
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="reset-result">
            <p className="text-sm font-bold text-emerald-800 mb-2">Senha resetada com sucesso!</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-slate-500">Usuario:</span> <strong>{result.user_name || result.user_email}</strong></div>
              <div><span className="text-slate-500">Tipo:</span> <strong>{result.role === 'gym' ? 'Academia' : 'Empresa'}</strong></div>
              <div><span className="text-slate-500">Email:</span> <strong>{result.user_email}</strong></div>
              <div>
                <span className="text-slate-500">Nova Senha:</span>{' '}
                <code className="bg-white px-2 py-0.5 rounded border border-emerald-300 font-mono text-base font-black text-emerald-700 select-all" data-testid="new-password-display">
                  {result.new_password}
                </code>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2 font-medium">Anote a senha antes de fechar. O usuario deve alterar no primeiro acesso.</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => { navigator.clipboard.writeText(result.new_password); toast.success('Senha copiada!'); }}>
              Copiar Senha
            </Button>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por email, nome ou empresa..." className="pl-10" data-testid="reset-search" />
        </div>

        {/* Users list */}
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-6 text-slate-400 text-sm">Nenhum usuario encontrado</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {filtered.map(u => (
              <div key={u.user_id} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3" data-testid={`reset-user-${u.user_id}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${u.role === 'gym' ? 'bg-orange-100' : 'bg-blue-100'}`}>
                    {u.role === 'gym' ? <Dumbbell className="w-4 h-4 text-orange-600" /> : <Building2 className="w-4 h-4 text-blue-600" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{u.name || u.email}</p>
                    <p className="text-[11px] text-slate-500 truncate">{u.email} | {u.entity_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.role === 'gym' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{u.role_label}</span>
                  <Button variant="outline" size="sm" onClick={() => handleReset(u.user_id)} disabled={resetting === u.user_id}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`reset-btn-${u.user_id}`}>
                    {resetting === u.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1" />}
                    Resetar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AdminSecurity = () => {
  const [is2FAActive, setIs2FAActive] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    adminAxios.get(`${API}/admin/2fa/status`).then(r => {
      setIs2FAActive(r.data.is_active);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSetup = async () => {
    setActionLoading(true);
    try {
      const res = await adminAxios.post(`${API}/admin/2fa/setup`);
      setSetupData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar 2FA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (confirmCode.length < 6) return;
    setActionLoading(true);
    try {
      await adminAxios.post(`${API}/admin/2fa/confirm`, { code: confirmCode });
      toast.success('2FA ativado com sucesso!');
      setIs2FAActive(true);
      setSetupData(null);
      setConfirmCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length < 6) return;
    setActionLoading(true);
    try {
      await adminAxios.post(`${API}/admin/2fa/disable`, { code: disableCode });
      toast.success('2FA desativado!');
      setIs2FAActive(false);
      setDisableCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-security-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
          <KeyRound className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Segurança</h2>
          <p className="text-sm text-slate-500">Autenticação de dois fatores (2FA)</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Google Authenticator
            </div>
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${is2FAActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="2fa-status-badge">
              {is2FAActive ? 'ATIVO' : 'INATIVO'}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!is2FAActive && !setupData && (
            <div>
              <p className="text-sm text-slate-600 mb-4">
                A autenticação de dois fatores adiciona uma camada extra de segurança ao seu login.
                Você precisará do aplicativo Google Authenticator para gerar códigos de verificação.
              </p>
              <Button onClick={handleSetup} disabled={actionLoading} data-testid="2fa-setup-btn" className="bg-blue-600 hover:bg-blue-700">
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Configurar 2FA
              </Button>
            </div>
          )}

          {setupData && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-blue-800 mb-2">Passo 1: Escaneie o QR Code</p>
                <p className="text-xs text-blue-700 mb-3">Abra o Google Authenticator e escaneie o código abaixo:</p>
                <div className="flex justify-center bg-white rounded-lg p-4">
                  <img src={setupData.qr_code} alt="QR Code 2FA" className="w-48 h-48" data-testid="2fa-qr-code" />
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-600 mb-1">Chave manual (caso não consiga escanear):</p>
                <code className="text-sm font-mono bg-white px-3 py-1.5 rounded border border-slate-200 block text-center select-all" data-testid="2fa-secret-key">
                  {setupData.secret}
                </code>
              </div>

              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">Passo 2: Confirme com o código</p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    data-testid="2fa-confirm-input"
                    className="text-center text-lg tracking-widest font-mono max-w-[180px]"
                  />
                  <Button onClick={handleConfirm} disabled={actionLoading || confirmCode.length < 6} data-testid="2fa-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700">
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ativar'}
                  </Button>
                </div>
              </div>

              <button onClick={() => setSetupData(null)} className="text-xs text-slate-500 hover:text-slate-700">Cancelar</button>
            </div>
          )}

          {is2FAActive && (
            <div>
              <p className="text-sm text-emerald-700 mb-4">
                2FA está ativo. Para desativar, insira o código atual do Google Authenticator.
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  data-testid="2fa-disable-input"
                  className="text-center text-lg tracking-widest font-mono max-w-[180px]"
                />
                <Button onClick={handleDisable} disabled={actionLoading || disableCode.length < 6} variant="destructive" data-testid="2fa-disable-btn">
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desativar 2FA'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unverified Users */}
      <UnverifiedUsersSection />

      {/* Password Reset */}
      <PasswordResetSection />

      {/* Email Fix */}
      <EmailFixSection />
    </div>
  );
};

// ==================== AUDIT LOG ====================


// ==================== MAIN LAYOUT ====================
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [adminAuth, setAdminAuth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    adminAxios.get(`${API}/admin/me`).then(r => {
      setAdminAuth(r.data);
      setLoading(false);
    }).catch(() => {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const navItems = [
    { path: '/admin', label: t('adminNav.panel'), icon: <Home className="w-5 h-5" /> },
    { path: '/admin/requests', label: t('adminNav.requests'), icon: <ClipboardCheck className="w-5 h-5" /> },
    { path: '/admin/companies', label: t('adminNav.companies'), icon: <Building2 className="w-5 h-5" /> },
    { path: '/admin/gyms', label: t('adminNav.gyms'), icon: <Dumbbell className="w-5 h-5" /> },
    { path: '/admin/students', label: t('adminNav.students'), icon: <Users className="w-5 h-5" /> },
    { path: '/admin/personal-approvals', label: 'Aprovação PF', icon: <UserCheck className="w-5 h-5" /> },
    { path: '/admin/professional-approvals', label: 'Profissionais', icon: <Stethoscope className="w-5 h-5" /> },
    { path: '/admin/vitalpass', label: 'Controle VITALPASS', icon: <Ticket className="w-5 h-5" /> },
    { path: '/admin/payouts', label: 'Repasses', icon: <DollarSign className="w-5 h-5" /> },
    { path: '/admin/checkins', label: t('adminNav.checkins'), icon: <Activity className="w-5 h-5" /> },
    { path: '/admin/payments', label: t('adminNav.payments'), icon: <CreditCard className="w-5 h-5" /> },
    { path: '/admin/notifications', label: t('adminNav.notifications'), icon: <Bell className="w-5 h-5" /> },
    { path: '/admin/coparticipations', label: t('adminNav.coparticipations'), icon: <UserPlus className="w-5 h-5" /> },
    { path: '/admin/financial', label: t('adminNav.financial'), icon: <DollarSign className="w-5 h-5" /> },
    { path: '/admin/content', label: t('adminNav.content'), icon: <Film className="w-5 h-5" /> },
    { path: '/admin/security', label: t('adminNav.security'), icon: <KeyRound className="w-5 h-5" /> },
    { path: '/admin/audit-log', label: t('adminNav.auditLog'), icon: <ScrollText className="w-5 h-5" /> },
    { path: '/admin/metrics', label: t('adminNav.performance'), icon: <Gauge className="w-5 h-5" /> },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Toaster position="top-center" />

      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-60 bg-slate-950 z-40 hidden lg:flex flex-col">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-7 h-7 text-blue-400" />
            <span className="text-lg font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <Shield className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs text-slate-400 font-medium">Admin</span>
          </div>
        </div>

        <nav
          className="flex-1 min-h-0 overflow-y-auto px-3 space-y-1 pb-3"
          data-testid="admin-sidebar-nav"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent' }}
        >
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.path === '/admin'}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-slate-800 text-white font-medium' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}>
              {item.icon}{item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800 space-y-1">
          <div className="px-3 py-1">
            <LanguageSwitcher />
          </div>
          <div className="flex items-center gap-3 px-3 py-2 [&_button]:hover:bg-slate-800 [&_svg.lucide-bell]:text-slate-400">
            <NotificationBell />
            <span className="text-sm text-slate-400">{t('common.alerts')}</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-slate-800/50 w-full" data-testid="admin-logout-btn">
            <LogOut className="w-5 h-5" />{t('common.logout')}
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden bg-slate-950 p-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-blue-400" />
          <span className="text-base font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Vital Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <NotificationBell />
          <button onClick={handleLogout} className="text-slate-400 hover:text-white p-2"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>

      {/* Mobile nav */}
      <nav className="lg:hidden bg-white border-b border-slate-200 flex overflow-x-auto sticky top-[56px] z-20 scrollbar-hide">
        {navItems.map(item => (
          <NavLink key={item.path} to={item.path} end={item.path === '/admin'}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-2 min-w-[52px] text-center border-b-2 shrink-0 ${isActive ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-slate-500'}`}>
            {item.icon}<span className="text-[9px] leading-tight whitespace-nowrap">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Content */}
      <main className="lg:ml-60 p-4 md:p-6 lg:p-8">
        <Routes>
          <Route path="/" element={<AdminOverview />} />
          <Route path="/requests" element={<AdminRequests />} />
          <Route path="/companies" element={<AdminCompanies />} />
          <Route path="/gyms" element={<AdminGyms />} />
          <Route path="/students" element={<AdminStudents />} />
          <Route path="/checkins" element={<AdminCheckins />} />
          <Route path="/payments" element={<AdminPayments />} />
          <Route path="/notifications" element={<AdminNotifications />} />
          <Route path="/coparticipations" element={<AdminCoparticipations />} />
          <Route path="/financial" element={<AdminFinancial />} />
          <Route path="/content" element={<AdminContent />} />
          <Route path="/security" element={<AdminSecurity />} />
          <Route path="/audit-log" element={<AdminAuditLog />} />
          <Route path="/metrics" element={<AdminMetrics />} />
          <Route path="/personal-approvals" element={<AdminPersonalApprovals />} />
          <Route path="/professional-approvals" element={<AdminProfessionalApprovals />} />
          <Route path="/vitalpass" element={<AdminVitalPassControl />} />
          <Route path="/payouts" element={<AdminPayouts />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
